<template>
  <div class="p2p-page narrow">
    <div class="p2p-stack" style="gap:6px">
      <h1 class="p2p-h1">Settings</h1>
      <p class="p2p-lead">Your time zone, which emails we send you, and how your data is kept.</p>
    </div>
    <div v-if="error" class="p2p-alert bad">{{ error }}</div>
    <div v-if="saved" class="p2p-alert good">Settings saved.</div>
    <div v-if="loading" class="p2p-skeleton" style="height:260px"></div>
    <template v-else>
      <section class="p2p-card" aria-labelledby="zone-title">
        <h2 id="zone-title" class="p2p-h2">Time zone</h2>
        <p class="p2p-muted" style="margin:0">New autopilots and schedules start in this time zone.</p>
        <label class="p2p-label">Time zone
          <select v-model="form.timezone" class="p2p-select"><option v-for="zone in zones" :key="zone" :value="zone">{{ zone }}</option></select>
        </label>
      </section>

      <section class="p2p-card" aria-labelledby="email-title">
        <h2 id="email-title" class="p2p-h2">Email me when</h2>
        <p class="p2p-muted" style="margin:0">You always see these in your notifications. Billing and security emails are always sent.</p>
        <label v-for="item in categories" :key="item.key" class="p2p-check p2p-setting">
          <input v-model="form.email[item.key]" type="checkbox">
          <span class="p2p-stack" style="gap:2px"><strong>{{ item.label }}</strong><span class="p2p-faint">{{ item.help }}</span></span>
        </label>
      </section>

      <section class="p2p-card" aria-labelledby="video-title">
        <h2 id="video-title" class="p2p-h2">AI video limit</h2>
        <p class="p2p-muted" style="margin:0">AI video uses the most credits. Set a monthly limit and new AI videos are refused once it is reached. Leave empty for no limit.</p>
        <label class="p2p-label">Credits per month for AI video
          <input v-model="form.ai_video_monthly_cap" type="number" min="0" step="1" class="p2p-input" style="max-width:220px" placeholder="No limit">
        </label>
      </section>

      <section class="p2p-card" aria-labelledby="quality-title">
        <h2 id="quality-title" class="p2p-h2">Help improve the writing</h2>
        <label class="p2p-check p2p-setting">
          <input v-model="form.quality_consent" type="checkbox">
          <span class="p2p-stack" style="gap:2px"><strong>Let our team review anonymised samples of my posts</strong>
            <span class="p2p-faint">A few generated posts may be rated by our team to improve quality. Product names, links, emails and handles are hidden, and nothing shows who you are. Off unless you turn it on; turning it off stops it at once.</span></span>
        </label>
      </section>

      <div class="p2p-row">
        <button type="button" class="p2p-btn" :disabled="busy" @click="save">{{ busy ? 'Saving…' : 'Save settings' }}</button>
      </div>

      <section class="p2p-card" aria-labelledby="reviewers-title">
        <h2 id="reviewers-title" class="p2p-h2">Reviewers</h2>
        <p class="p2p-muted" style="margin:0">Invite someone to approve or reject your autopilot posts. They sign in with their own account and see only your approvals. They cannot edit your posts, spend your credits on anything else or change your connected accounts.</p>
        <div v-for="item in team.reviewers" :key="item.id" class="p2p-between" style="gap:8px">
          <span style="overflow-wrap:anywhere">{{ item.email }}</span>
          <span class="p2p-row" style="gap:8px">
            <span :class="['p2p-chip', item.status === 'active' ? 'good' : '']">{{ item.status === 'active' ? 'Reviewing' : 'Invited' }}</span>
            <button type="button" class="p2p-btn sm ghost" :disabled="busy" @click="removeReviewer(item.id)">Remove</button>
          </span>
        </div>
        <div class="p2p-row" style="align-items:flex-end">
          <label class="p2p-label" style="flex:1 1 240px">Email address
            <input v-model="inviteEmail" type="email" class="p2p-input" placeholder="colleague@company.com" autocomplete="off">
          </label>
          <button type="button" class="p2p-btn sm" :disabled="busy || !inviteEmail.trim()" @click="inviteReviewer">Invite reviewer</button>
        </div>
        <div v-if="invite" class="p2p-alert">
          <div class="p2p-stack" style="gap:6px">
            <strong>Send this link to {{ invite.email }}. It works for {{ invite.days }} days, only for an account with that email address.</strong>
            <code class="p2p-mono" style="overflow-wrap:anywhere">{{ invite.url }}</code>
            <button type="button" class="p2p-btn sm soft" style="align-self:flex-start" @click="copyInvite">{{ copied ? 'Copied' : 'Copy link' }}</button>
          </div>
        </div>
        <template v-if="team.reviewing_for.length">
          <span class="p2p-text">You review autopilot posts for</span>
          <div v-for="item in team.reviewing_for" :key="'for-' + item.link_id" class="p2p-between" style="gap:8px">
            <router-link :to="'/approvals?owner=' + item.owner_id">{{ item.email }}</router-link>
            <button type="button" class="p2p-btn sm ghost" :disabled="busy" @click="removeReviewer(item.link_id)">Stop reviewing</button>
          </div>
        </template>
      </section>

      <section class="p2p-card" aria-labelledby="data-title">
        <h2 id="data-title" class="p2p-h2">Your data</h2>
        <ul class="p2p-facts">
          <li class="p2p-text">Your social account passwords and tokens are never stored here. Post for Me, our publishing partner, holds the connections.</li>
          <li v-if="!paidOnly" class="p2p-text">A sample you never saved is deleted after seven days.</li>
          <li class="p2p-text">A deleted project's files are permanently removed seven days after you delete it. Published posts stay on the networks.</li>
          <li class="p2p-text">Your content is never used to train shared AI models.</li>
        </ul>
        <div class="p2p-row">
          <router-link to="/profile" class="p2p-btn sm outline">Account and password</router-link>
          <router-link to="/billing" class="p2p-btn sm outline">Billing</router-link>
          <router-link :to="{ path: '/help', query: { topic: 'data_export' }, hash: '#contact' }" class="p2p-btn sm ghost">Ask for a copy of my data</router-link>
          <router-link :to="{ path: '/help', query: { topic: 'account_erasure' }, hash: '#contact' }" class="p2p-btn sm ghost">Delete my account</router-link>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import { get, put, post, remove, message } from '../api';

export default {
  name: 'Settings',
  data: () => ({ loading: true, busy: false, error: '', saved: false,
    team: { reviewers: [], reviewing_for: [] }, inviteEmail: '', invite: null, copied: false,
    form: { timezone: 'UTC', email: { approvals: true, failures: true, automation: true, connections: true, weekly: false }, ai_video_monthly_cap: '',
      quality_consent: false },
    categories: [
      { key: 'approvals', label: 'A post waits for my approval', help: 'Autopilot posts you review before they go out, and ones skipped because time ran out.' },
      { key: 'failures', label: 'A scheduled post fails', help: 'A network refused a post, or a post is waiting for credits.' },
      { key: 'automation', label: 'An autopilot pauses', help: 'Out of credits, or several runs in a row did not work.' },
      { key: 'connections', label: 'An account needs reconnecting', help: 'A network signed us out, so posts to it cannot go out.' },
      { key: 'weekly', label: 'A weekly summary', help: 'Monday mornings: what was written and published, and your best post. Off unless you turn it on.' }
    ] }),
  computed: {
    paidOnly() { return this.$store.getters.forcePaidPlans; },
    zones() {
      const list = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
      const all = list.length ? list.slice() : ['UTC'];
      if (all.indexOf(this.form.timezone) < 0) all.unshift(this.form.timezone);
      return all;
    }
  },
  async mounted() {
    this.loadTeam();
    try {
      const prefs = await get('/preferences');
      const browser = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      this.form = { timezone: prefs.timezone === 'UTC' && !prefs.defaults.formats ? browser : prefs.timezone, email: { ...prefs.email },
        ai_video_monthly_cap: prefs.ai_video_monthly_cap ? String(prefs.ai_video_monthly_cap) : '', quality_consent: !!prefs.quality_consent };
    } catch (error) {
      this.error = message(error);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async loadTeam() {
      try { this.team = await get('/reviewers'); } catch (error) { this.error = message(error); }
    },
    async inviteReviewer() {
      this.busy = true;
      this.error = '';
      try {
        const result = await post('/reviewers', { email: this.inviteEmail.trim() });
        this.invite = { email: result.reviewer.email, url: result.invite_url, days: result.expires_in_days };
        this.copied = false;
        this.inviteEmail = '';
        await this.loadTeam();
      } catch (error) {
        this.error = message(error);
      } finally {
        this.busy = false;
      }
    },
    async removeReviewer(id) {
      this.busy = true;
      this.error = '';
      try {
        await remove('/reviewers/' + id);
        this.invite = null;
        await this.loadTeam();
      } catch (error) {
        this.error = message(error);
      } finally {
        this.busy = false;
      }
    },
    async copyInvite() {
      try { await navigator.clipboard.writeText(this.invite.url); this.copied = true; } catch (error) { this.copied = false; }
    },
    async save() {
      this.busy = true;
      this.error = '';
      this.saved = false;
      try {
        const cap = String(this.form.ai_video_monthly_cap || '').trim();
        const prefs = await put('/preferences', { timezone: this.form.timezone, email: this.form.email,
          ai_video_monthly_cap: cap ? Math.max(0, Math.floor(Number(cap))) : null, quality_consent: !!this.form.quality_consent });
        this.form = { timezone: prefs.timezone, email: { ...prefs.email },
          ai_video_monthly_cap: prefs.ai_video_monthly_cap ? String(prefs.ai_video_monthly_cap) : '', quality_consent: !!prefs.quality_consent };
        this.saved = true;
      } catch (error) {
        this.error = message(error);
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>

<style>
.p2p-setting{align-items:flex-start!important;gap:12px;padding:10px 0;border-top:1px solid var(--p2p-line)}
.p2p-setting input{margin-top:3px}
</style>
